// src/components/DashboardErrorBoundary.jsx
import React, { Component } from 'react';
import { useLocation } from 'react-router-dom';

// Inner class component (unchanged logic)
class ErrorBoundaryInner extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidMount() {
        sessionStorage.removeItem('dashboard_chunk_failed_reload');
    }

    componentDidUpdate(prevProps) {
        // Reset when location changes (user navigated away)
        if (this.state.hasError && prevProps.location !== this.props.location) {
            this.setState({ hasError: false, error: null });
        }
    }

    componentDidCatch(error, info) {
        console.error('[Dashboard] Render error:', error, info);

        if (error?.message && /Failed to fetch dynamically imported module/i.test(error.message)) {
            if (!sessionStorage.getItem('dashboard_chunk_failed_reload')) {
                sessionStorage.setItem('dashboard_chunk_failed_reload', 'true');
                window.location.reload();
                return;
            }
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    padding: '2rem',
                    textAlign: 'center',
                    minHeight: '50vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div className="text-6xl mb-4">⚠️</div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Something went wrong loading this page.</h2>
                    <p style={{ color: '#666' }}>
                        Try going back or refreshing the page.
                    </p>
                    <button
                        onClick={() => this.setState({ hasError: false, error: null })}
                        style={{
                            marginTop: '1rem',
                            padding: '0.5rem 1.5rem',
                            background: '#3B82F6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 500
                        }}
                    >
                        Try Again
                    </button>
                    {process.env.NODE_ENV !== 'production' && this.state.error && (
                        <pre className="mt-4 p-4 bg-red-50 text-red-700 text-sm rounded-lg max-w-lg overflow-auto text-left">
                            {this.state.error.toString()}
                        </pre>
                    )}
                </div>
            );
        }
        return this.props.children;
    }
}

// Outer functional wrapper that injects location as prop
const DashboardErrorBoundary = ({ children }) => {
    const location = useLocation();
    return (
        <ErrorBoundaryInner location={location}>
            {children}
        </ErrorBoundaryInner>
    );
};

export default DashboardErrorBoundary;
